import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { ContractByEntity } from 'app/core/models/entityById.model';
import { Entity } from 'app/features/settings/models/entity.model';
import { createInsertBody, createPatchBody } from 'app/shared/forms/utils/forms.utils';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { AcctInvoiceReversal } from '../models/acct-invoice-reversal.model';
import { AcctInvoicesByContractParameters } from '../models/acct-invoices-by-contract-parameters.model';
import { AcctInvoice } from '../models/acct-invoices.model';
import { AircraftWarrantyHistory } from '../models/aircraft-warranty-history.model';
import { ContractAccountManagerHistory } from '../models/contract-account-manager-history.model';
import { ContractBillingEntity } from '../models/contract-billing-entity.model';
import { ContractComment } from '../models/contract-comment.model';
import { ContractEntity } from '../models/contract-entity.model';
import { ContractEstimatedValue } from '../models/contract-estimated-value.model';
import {
  ContractFlightCardComponent,
  ContractFlightCardComponentInsert,
} from '../models/contract-flight-card-component.model';
import { ContractFlightCardComponentType } from '../models/contract-flight-card-component-type.model';
import { ContractPartConsideration } from '../models/contract-part-consideration.model';
import {
  ContractProduct,
  ContractProductDetailManuipulation as ContractProductDetailManipulation,
  DetailPutResponse,
} from '../models/contract-product.model';
import { ContractSearchPayload } from '../models/contract-search-payload.model';
import { ContractStatusHistory } from '../models/contract-status-history.model';
import { Contract } from '../models/contract.model';
import { ChartNote } from '../models/chart-note.model';
import { FlightCard } from '../models/flight-card.model';
import { NextFlightCard } from '../models/next-flight-card.model';
import { Note } from '../models/note.model';
import { Task } from '../models/task.model';
import { ContractTransfer, ContractTransferReturn } from '../models/contract-transfer.model';
import { ContractEntityPostReturn } from '../models/contract-entity-post-return.model';
import { MasterBillTo } from '../models/master-bill-to.model';
import { ContractDiscount } from '../models/contract-discount.model';
import { ContractProductSimple } from '../models/contract-products-simple';
import { TaskCommentHistory } from '../models/task-comment-history.model';
import { MasterEntity } from '../models/master-entities.model';
import { ContractDiscountUsage } from '../models/contract-discount-usage.model';
import { FileInfos } from '../models/file-infos.model';
import { ContractTransferHistoryEntity } from '../models/contract-transfer-history-entities.model';
import { CreateAgreement } from '../models/create-agreement.model';
import { ChartNoteSignature } from '../models/chart-note-signature.model';
import { ExternalWebUser } from 'app/features/settings/models/external-web-user.model';
import { ChartNotePdfUri } from '../models/chart-note-pdf-uri.model';
import { AgreementViewList } from '../models/agreement-view-list.model';
import { ChartNoteClientAccess } from '../models/chartnote-client-access.model';
import { ContractCurrentValue } from 'app/core/models/contract-current-value.model';
import { ContractProductSummary } from 'app/core/models/contract-product-summary.model';
import { OrderParameter } from 'app/features/settings/models/order-parameter.model';
import { ContractProductDetailDiscount } from '../models/contract-product-detail-discount.model';
import { ActivationCheck } from '../models/activation-check.model';
import { FlightCardDetail } from '../models/flight-card-detail.model';
import { AircraftInWarrantyStatus } from 'app/features/settings/models/aircraft-in-warranty-status.model';
import { ReportRequest } from '../models/report-request.model';
import { FlightCardTemReturn } from '../models/flight-card-tem-return.model';
import { AdobeRefreshTokenInfo } from '../models/adobe-refresh-token-info.model';
import { ContractSummaryToDate } from '../models/contract-summary-to-date.model';
import { ContractCaptureTypeHistory } from '../models/contract-capture-types-history.model';
import { EstimateCalculation } from '../models/estimate-calculation.model';
import { ContractHelperService } from './contract-helper.service';
import { LatestContractInvoices } from '../models/latest-contract-invoices';

@Injectable({
  providedIn: 'root',
})
export class ContractHttpService {
  private baseUrl = environment.apiUrl;
  private baseReportUrl = environment.reportApiUrl;
  constructor(
    private http: HttpClient,
    private contractHelper: ContractHelperService
  ) {}

  getContracts(): Observable<Contract[]> {
    const url = `${this.baseUrl}contracts`;
    return this.http.get<Contract[]>(url);
  }

  getSearchedContracts(searchParams: ContractSearchPayload): Observable<Contract[]> {
    let params = new HttpParams();
    Object.entries(searchParams ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (value instanceof Array) {
          value.forEach(item => {
            params = params.append(key, item.toString());
          });
        } else {
          params = params.append(key, value.toString());
        }
      }
    });
    const url = this.contractHelper.isRenewalMode()
      ? `${this.baseUrl}contracts/searchrenewals`
      : this.contractHelper.isAttritionMode()
        ? `${this.baseUrl}contracts/searchattrition`
        : `${this.baseUrl}contracts/search`;
    return this.http.get<Contract[]>(url, { params });
  }

  getContractById(id: number): Observable<Contract> {
    const url = `${this.baseUrl}contracts/${id}`;
    return this.http.get<Contract>(url);
  }

  insertContract(form: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  patchContract(form: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  deleteContract(id: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${id}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getNotes(contractId: number): Observable<Note[]> {
    const url = `${this.baseUrl}contract/${contractId}/notes`;
    return this.http.get<Note[]>(url);
  }

  insertNote(form: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/notes`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  updateNote(form: UntypedFormGroup): Observable<any> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/notes/${form.get('noteId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  deleteNote(noteId: number, contractId: number): Observable<any> {
    const url = `${this.baseUrl}contract/${contractId}/notes/${noteId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getContractAccountManagerHistory(
    contractId: number
  ): Observable<ContractAccountManagerHistory[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractaccountmanagerhistories`;
    return this.http.get<ContractAccountManagerHistory[]>(url);
  }

  insertContractAccountManagerHistory(
    form: UntypedFormGroup,
    contractId: number
  ): Observable<number> {
    const url = `${this.baseUrl}contracts/${contractId}/contractaccountmanagerhistories`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  getContractComments(contractId: number): Observable<ContractComment[]> {
    const url = `${this.baseUrl}contract/${contractId}/contractcomments`;
    return this.http.get<ContractComment[]>(url);
  }

  insertContractComment(form: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/contractcomments`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  updateContractComment(form: UntypedFormGroup): Observable<any> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/contractcomments/${form.get('id')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  deleteContractComment(id: number, contractId: number): Observable<any> {
    const url = `${this.baseUrl}contract/${contractId}/contractcomments/${id}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getContractStatusHistory(contractId: number): Observable<ContractStatusHistory[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractstatushistories`;
    return this.http.get<ContractStatusHistory[]>(url);
  }

  insertContractStatusHistory(form: UntypedFormGroup, contractId: number): Observable<number> {
    const url = `${this.baseUrl}contracts/${contractId}/contractstatushistories`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  updateContractStatusHistory(form: UntypedFormGroup, contractId: number) {
    const url = `${this.baseUrl}contracts/${contractId}/contractstatushistories/${form.get('id')?.value}`;
    return this.http.patch<number>(url, createPatchBody(form));
  }

  getAircraftWarrantyHistory(aircraftId: number): Observable<AircraftWarrantyHistory[]> {
    const url = `${this.baseUrl}aircrafts/aircraftwarrantyhistories/${aircraftId}`;
    return this.http.get<AircraftWarrantyHistory[]>(url);
  }

  updateAircraftWarranty(form: UntypedFormGroup): Observable<any> {
    const url = `${this.baseUrl}aircrafts/${form.get('aircraftId')?.value}/warranty`;
    return this.http.put<any>(url, form.value);
  }

  getContractEstimateValue(contractId: number): Observable<ContractEstimatedValue[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractestimatedvalues`;
    return this.http.get<ContractEstimatedValue[]>(url);
  }

  insertContractEstimateValue(form: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}/contractestimatedvalues`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  getTasks(params: HttpParams): Observable<Task[]> {
    const url = `${this.baseUrl}tasks`;
    return this.http.get<Task[]>(url, { params });
  }

  insertContractTasks(form: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/tasks`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  deleteContractTasks(taskId: number, contractId: number): Observable<any> {
    const url = `${this.baseUrl}contract/${contractId}/tasks/${taskId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  patchContractTasks(form: UntypedFormGroup): Observable<any> {
    const url = `${this.baseUrl}contract/${form.get('contractId')?.value}/tasks/${form.get('taskId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  getContractEntities(contractId: number): Observable<ContractEntity[]> {
    const url = `${this.baseUrl}contract/${contractId}/contractentities`;
    return this.http.get<ContractEntity[]>(url, { responseType: 'json' });
  }

  insertContractEntities(formGroup: FormGroup): Observable<ContractEntityPostReturn> {
    const url = `${this.baseUrl}contract/${formGroup.get('contractId')?.value}/contractentities`;
    return this.http.post<ContractEntityPostReturn>(url, createInsertBody(formGroup));
  }

  putContractEntities(formGroup: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contract/${formGroup.get('contractId')?.value}/contractentities/${formGroup.get('contractEntityId')?.value}`;
    return this.http.put<any>(url, formGroup.value);
  }

  deleteContractEntity(contractId: number, contractEntityId: number): Observable<any> {
    const url = `${this.baseUrl}contract/${contractId}/contractentities/${contractEntityId}`;
    return this.http.delete(url);
  }

  insertContractEntityOnly(formGroup: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contract/${formGroup.get('contractId')?.value}/contractentities/contractentityonly`;
    return this.http.post<number>(url, createInsertBody(formGroup));
  }

  getSearchEntities(pattern: string, toggle: number): Observable<Entity[]> {
    let params = new HttpParams();
    if (pattern) {
      params = params.append('searchText', pattern.toString());
    }
    if (toggle) {
      params = params.append('toggle', toggle.toString());
    }
    const url = `${this.baseUrl}entities/search`;
    return this.http.get<Entity[]>(url, { params, responseType: 'json' });
  }

  getContractPartConsiderations(contractId: number): Observable<ContractPartConsideration[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractpartconsiderations`;
    return this.http.get<ContractPartConsideration[]>(url);
  }

  getContractsByEntity(id: number): Observable<ContractByEntity[]> {
    const url = `${this.baseUrl}contracts/byentity/${id}`;
    return this.http.get<ContractByEntity[]>(url);
  }

  insertContractPartConsideration(form: UntypedFormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}/contractpartconsiderations`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  deleteContractPartConsideration(id: number, contractId: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractpartconsiderations/${id}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  patchContractPartConsideration(form: UntypedFormGroup, contractId: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractpartconsiderations/${form.get('id')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  getContractBillingEntities(contractEntityId: number): Observable<ContractBillingEntity[]> {
    const url = `${this.baseUrl}contractbillingentities/${contractEntityId}`;
    return this.http.get<ContractBillingEntity[]>(url);
  }

  insertContractBillingEntity(formGroup: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${formGroup.get('contractId')?.value}/contractbillingentities`;
    return this.http.post<number>(url, createInsertBody(formGroup));
  }

  patchContractBillingEntity(formGroup: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${formGroup.get('contractId')?.value}/contractbillingentities/${formGroup.get('id')?.value}`;
    return this.http.patch<any>(url, createPatchBody(formGroup));
  }

  deleteContractBillingEntity(contractId: number, id: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractbillingentities/${id}`;
    return this.http.delete(url);
  }

  getContractBillingEntitiesForContract(contractId: number): Observable<ContractBillingEntity[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractbillingentities`;
    return this.http.get<ContractBillingEntity[]>(url);
  }

  getMasterBillToEntities(pattern: string, toggle: number): Observable<MasterBillTo[]> {
    let params = new HttpParams();
    if (pattern) {
      params = params.append('searchText', pattern.toString());
    }
    if (toggle) {
      params = params.append('toggle', toggle.toString());
    }
    const url = `${this.baseUrl}masterbilltos/search`;
    return this.http.get<MasterBillTo[]>(url, { params, responseType: 'json' });
  }

  getContractProducts(contractId: number): Observable<ContractProduct[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractproducts`;
    return this.http.get<ContractProduct[]>(url);
  }

  insertContractProduct(formGroup: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${formGroup.get('contractId')?.value}/contractproducts`;
    return this.http.post<number>(url, createInsertBody(formGroup));
  }

  patchContractProduct(formGroup: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${formGroup.get('contractId')?.value}/contractproducts/${formGroup.get('contractProductId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(formGroup));
  }

  putContractProductDetail(
    contractproductId: number,
    payload: ContractProductDetailManipulation[]
  ): Observable<DetailPutResponse[]> {
    const url = `${this.baseUrl}contractproducts/${contractproductId}/contractproductdetails`;
    return this.http.put<DetailPutResponse[]>(url, payload);
  }

  insertContractProductBundle(
    contractId: number,
    formGroup: FormGroup
  ): Observable<ContractProduct[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractproducts/productbundle`;
    return this.http.post<ContractProduct[]>(url, createInsertBody(formGroup));
  }

  getContractFlightCardComponentTypes(id: number): Observable<number[]> {
    const url = `${this.baseUrl}sysflightcardcomponenttypes/${id}`;
    return this.http.get<number[]>(url);
  }

  getFlightCardComponentTypesBasedOnContract(
    contractId: number
  ): Observable<ContractFlightCardComponentType[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractflightcardcomponenttypes`;
    return this.http.get<ContractFlightCardComponentType[]>(url);
  }

  generateContractNumber(serialNumber: string): Observable<string> {
    const url = `${this.baseUrl}contracts/generatecontractnumber`;
    return this.http.post<string>(url, { serialNumber }, { responseType: 'text' as 'json' });
  }

  validContractPair(contractNumber: {
    contractProgramId: number;
    aircraftId: number;
  }): Observable<boolean> {
    const url = `${this.baseUrl}contracts/validcontractpair`;
    return this.http.post<boolean>(url, contractNumber);
  }

  getFlightCardDetailsByContract(contractId: number): Observable<FlightCard[]> {
    const url = `${this.baseUrl}contracts/${contractId}/flightcarddetails`;
    return this.http.get<FlightCard[]>(url);
  }

  getContractFlightCardComponents(contractId: number): Observable<ContractFlightCardComponent[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractflightcardcomponents`;
    return this.http.get<ContractFlightCardComponent[]>(url);
  }

  getChartNotes(contractId: number): Observable<ChartNote[]> {
    const url = `${this.baseUrl}chartnotes/contracts/${contractId}`;
    return this.http.get<ChartNote[]>(url);
  }

  insertChartNoteQuickForm(form: FormGroup): Observable<ChartNote> {
    const url = `${this.baseUrl}chartnotes/flexform`;
    return this.http.post<ChartNote>(url, createInsertBody(form));
  }

  insertChartNoteDocument(formData: FormData): Observable<ChartNote> {
    const headers = new HttpHeaders({ 'Skip-Interceptor': '' });
    const url = `${this.baseUrl}chartnotes/documents`;
    return this.http.post<ChartNote>(url, formData, { headers });
  }

  getChartNote(id: number, formType: string): Observable<ChartNote> {
    const url = `${this.baseUrl}chartnotes/${id}/${formType}`;
    return this.http.get<ChartNote>(url);
  }

  updateChartNoteDocument(id: number, formData: FormData): Observable<ChartNote> {
    const headers = new HttpHeaders({ 'Skip-Interceptor': '' });
    const url = `${this.baseUrl}chartnotes/${id}/documents`;
    return this.http.put<ChartNote>(url, formData, { headers });
  }

  updateChartNoteQuickForm(form: FormGroup): Observable<ChartNote> {
    const url = `${this.baseUrl}chartnotes/${form.get('chartNoteId')?.value}/flexform`;
    return this.http.put<ChartNote>(url, form.value);
  }

  updateChartNoteSignOff(chartNoteId: number, form: FormGroup): Observable<any> {
    const url = `${this.baseUrl}chartnotes/${chartNoteId}/signoff`;
    return this.http.put<any>(url, form.value);
  }

  getSasUriForDocument(id: number): Observable<string> {
    const url = `${this.baseUrl}chartnotes/${id}/document`;
    return this.http.get<string>(url, { responseType: 'text' as 'json' });
  }

  getSasUriForPdf(id: number): Observable<string> {
    const url = `${this.baseUrl}chartnotes/${id}/sasuri`;
    return this.http.get<string>(url, { responseType: 'text' as 'json' });
  }

  insertChartNoteAddendum(formGroup: FormGroup): Observable<number> {
    const url = `${this.baseUrl}chartnotes/${formGroup.get('chartNoteId')?.value}/addendums`;
    return this.http.post<number>(url, createInsertBody(formGroup));
  }

  getFormHtml(nativeElement: any, title: any): string {
    nativeElement.querySelectorAll('input').forEach((el: any) => {
      el.setAttribute('value', el.value);
    });
    nativeElement.querySelectorAll('textarea').forEach((el: any) => {
      el.innerHTML = el.value;
    });
    const origin = environment.production
      ? location.origin
      : 'https://dev-ssics-staffportal.azurewebsites.net';

    const htmlStart = `<!doctype html>
            <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0">
              <title>${title}</title>
              <link rel="stylesheet" href="${origin}/fontello.css">
              <link rel="stylesheet" href="${origin}/dx.css">
              <link rel="stylesheet" href="${origin}/quick-forms-pdf.css">
            </head>
            <body>`,
      htmlMiddle = nativeElement.outerHTML,
      htmlEnd = '</body></html>',
      htmlResult = htmlStart + htmlMiddle + htmlEnd;

    return btoa(unescape(encodeURIComponent(htmlResult)));
  }

  insertAttachment(formData: FormData): Observable<ChartNote> {
    const headers = new HttpHeaders({ 'Skip-Interceptor': '' });
    const url = `${this.baseUrl}chartnotes/attachments`;
    return this.http.post<ChartNote>(url, formData, { headers });
  }

  putContractFlightCardComponents(
    contractId: number,
    payload: ContractFlightCardComponentInsert[]
  ): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractflightcardcomponents`;
    return this.http.put<any>(url, payload);
  }

  getNextSysMonthlyPeriod(contractId: number): Observable<number> {
    const url = `${this.baseUrl}contracts/${contractId}/nextperiod`;
    return this.http.get<number>(url);
  }

  insertFlightCard(payload: { [key: string]: any }): Observable<FlightCardTemReturn> {
    const url = `${this.baseUrl}contracts/${payload['contractId']}/flightcardstmp`;
    return this.http.post<FlightCardTemReturn>(url, payload);
  }

  confirmInsertFlightCard(contractId: number, flightcardTmpId: number): Observable<FlightCard> {
    const url = `${this.baseUrl}contracts/${contractId}/flightcardstmp/${flightcardTmpId}/confirm`;
    return this.http.post<FlightCard>(url, {});
  }

  deleteFlightCardTemp(contractId: number, flightcardTmpId: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/flightcardstmp/${flightcardTmpId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getNextFlightCard(contractId: number): Observable<NextFlightCard[]> {
    const url = `${this.baseUrl}contracts/${contractId}/nextflightcard`;
    return this.http.get<NextFlightCard[]>(url);
  }

  getAdjustmentFlightCard(
    contractId: number,
    sysMonthlyPeriodId: number
  ): Observable<NextFlightCard[]> {
    const url = `${this.baseUrl}contracts/${contractId}/sysMonthlyPeriod/${sysMonthlyPeriodId}/adjustmentflightcard`;
    return this.http.get<NextFlightCard[]>(url);
  }

  getFlightCardForMonthId(
    contractId: number,
    sysMonthlyPeriodId: number
  ): Observable<FlightCard[]> {
    const url = `${this.baseUrl}contracts/${contractId}/flightcards/sysmonthlyperiods/${sysMonthlyPeriodId}`;
    return this.http.get<FlightCard[]>(url);
  }

  getLatestAcctInvoices(contractId: number): Observable<LatestContractInvoices> {
    const url = `${this.baseUrl}acctinvoices/contracts/${contractId}/latest`;
    return this.http.get<LatestContractInvoices>(url);
  }

  getAcctInvoices(searchParams: AcctInvoicesByContractParameters): Observable<AcctInvoice[]> {
    let params = new HttpParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      params = value !== null ? params.append(key, value) : params;
    });
    const url = `${this.baseUrl}acctinvoices`;
    return this.http.get<AcctInvoice[]>(url, { params, responseType: 'json' });
  }

  getAcctInvoiceById(invoiceId?: number): Observable<AcctInvoice> {
    const url = `${this.baseUrl}acctinvoices/${invoiceId}`;
    return this.http.get<AcctInvoice>(url);
  }

  reverseAcctInvoice(payload: AcctInvoiceReversal): Observable<AcctInvoice> {
    const url = `${this.baseUrl}acctinvoices/${payload.invoiceId}/reversenorefund`;
    return this.http.put<AcctInvoice>(url, payload);
  }

  insertAcctInvoice(data: AcctInvoice): Observable<AcctInvoice> {
    const url = `${this.baseUrl}acctinvoices`;
    return this.http.post<AcctInvoice>(url, data);
  }

  updateAcctInvoice(data: AcctInvoice): Observable<any> {
    const url = `${this.baseUrl}acctinvoices/${data?.acctInvoiceId}`;
    return this.http.put<any>(url, data);
  }

  getLastEnteredContractFlightCard(contractId: number): Observable<FlightCard> {
    const url = `${this.baseUrl}contracts/${contractId}/latestcontractflightcard`;
    return this.http.get<FlightCard>(url);
  }

  getExternalWebUsersByContract(contractId: number): Observable<ExternalWebUser[]> {
    const url = `${this.baseUrl}contracts/${contractId}/externalwebusers`;
    return this.http.get<ExternalWebUser[]>(url);
  }

  insertExternalWebUserByContract(form: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}/externalwebusercontracts`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  patchExternalWebUserByContract(form: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}/externalwebusercontracts/${form.get('externalWebUserId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  deleteExternalWebUserByContract(contractId: number, externalWebUserId: number): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/externalwebusercontracts/${externalWebUserId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getContractTransfers(contractId: number): Observable<ContractTransfer[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contracttransferhistory`;
    return this.http.get<ContractTransfer[]>(url);
  }

  insertContractTransfer(form: FormGroup, contractId: number): Observable<ContractTransferReturn> {
    const url = `${this.baseUrl}contracts/${contractId}/contracttransferhistory`;
    return this.http.post<ContractTransferReturn>(url, createInsertBody(form));
  }

  getSearchMasterBillTo(pattern: string, toggle: number): Observable<MasterBillTo[]> {
    let params = new HttpParams();
    if (pattern) {
      params = params.append('searchText', pattern.toString());
    }
    if (toggle) {
      params = params.append('toggle', toggle.toString());
    }
    const url = `${this.baseUrl}masterbilltos/search`;
    return this.http.get<MasterBillTo[]>(url, { params, responseType: 'json' });
  }

  getSearchMasterEntities(pattern: string, toggle: number): Observable<MasterEntity[]> {
    let params = new HttpParams();
    if (pattern) {
      params = params.append('searchText', pattern.toString());
    }
    if (toggle) {
      params = params.append('toggle', toggle.toString());
    }
    const url = `${this.baseUrl}masterentities/search`;
    return this.http.get<MasterEntity[]>(url, { params, responseType: 'json' });
  }

  getContractDiscounts(contractId: number): Observable<ContractDiscount[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractdiscounts`;
    return this.http.get<ContractDiscount[]>(url);
  }

  getContractProductsByDiscountId(
    contractId: number,
    contractDiscountId: number
  ): Observable<ContractProductDetailDiscount[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractdiscounts/${contractDiscountId}/products`;
    return this.http.get<ContractProductDetailDiscount[]>(url);
  }

  getContractProductSimple(contractId: number): Observable<ContractProductSimple[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractproducts/simple`;
    return this.http.get<ContractProductSimple[]>(url);
  }

  insertContractDiscount(contractId: number, form: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${contractId}/contractdiscounts`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  patchContractDiscount(contractId: number, form: FormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractdiscounts/${form.get('contractDiscountId')?.value}`;
    return this.http.patch<any>(url, createPatchBody(form));
  }

  getContractDiscountUsages(contractId: number, id: number): Observable<ContractDiscountUsage[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractdiscounts/${id}/contractdiscountusage`;
    return this.http.get<ContractDiscountUsage[]>(url);
  }

  getTaskComments(taskId: number): Observable<TaskCommentHistory[]> {
    const url = `${this.baseUrl}tasks/${taskId}/taskcommenthistories`;
    return this.http.get<TaskCommentHistory[]>(url);
  }

  insertTaskComment(taskId: number, form: FormGroup): Observable<number> {
    const url = `${this.baseUrl}tasks/${taskId}/taskcommenthistories`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  getSignedDocumentById(chartNoteId: number): Observable<ChartNoteSignature> {
    const url = `${this.baseUrl}adobe/esign/chartnotes/${chartNoteId}`;
    return this.http.get<ChartNoteSignature>(url);
  }

  insertAgreement(chartNoteId: number, payload: CreateAgreement): Observable<{ id: string }> {
    const headers = new HttpHeaders({ 'Skip-Spinner': '' });
    const url = `${this.baseUrl}adobe/esign/chartnotes/${chartNoteId}/agreements`;
    return this.http.post<{ id: string }>(url, payload, { headers });
  }

  getAgreementUrlForSign(
    chartNoteId: number
  ): Observable<{ agreementViewList: AgreementViewList[] }> {
    const url = `${this.baseUrl}adobe/esign/chartnotes/${chartNoteId}/agreements/url`;
    return this.http.get<{ agreementViewList: AgreementViewList[] }>(url);
  }

  uploadDocumentForAdobe(chartNoteId: number): Observable<FileInfos> {
    const url = `${this.baseUrl}adobe/esign/chartnotes/${chartNoteId}/upload`;
    return this.http.post<FileInfos>(url, {});
  }

  getContractTransferHistoryEntities(
    contractId: number,
    contractTransferHistoryId: number
  ): Observable<ContractTransferHistoryEntity[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contracttransferhistory/${contractTransferHistoryId}/contractentities`;
    return this.http.get<ContractTransferHistoryEntity[]>(url);
  }

  getUpdatedPdfUriList(contractId: number): Observable<ChartNotePdfUri[]> {
    const url = `${this.baseUrl}chartnotes/contracts/${contractId}/pdfuri`;
    return this.http.get<ChartNotePdfUri[]>(url);
  }

  getChartnoteClientAccess(chartNoteId: number): Observable<ChartNoteClientAccess[]> {
    const url = `${this.baseUrl}chartnotes/${chartNoteId}/chartnoteclientaccess`;
    return this.http.get<ChartNoteClientAccess[]>(url);
  }

  postChartnoteClientAccess(chartNoteId: number, payload: any): Observable<number> {
    const url = `${this.baseUrl}chartnotes/${chartNoteId}/chartnoteclientaccess`;
    return this.http.post<number>(url, JSON.stringify(payload));
  }

  getSummary(contractId: number): Observable<ContractCurrentValue[]> {
    const url = `${this.baseUrl}contracts/${contractId}/summary`;
    return this.http.get<ContractCurrentValue[]>(url);
  }

  insertChartNotePrint(chartNoteId: number): Observable<any> {
    const url = `${this.baseUrl}chartnotes/${chartNoteId}/print`;
    return this.http.post<any>(url, {});
  }

  getProductSummaryForContract(id: number): Observable<ContractProductSummary> {
    const url = `${this.baseUrl}contracts/${id}/productsummary`;
    return this.http.get<ContractProductSummary>(url);
  }

  contractProductUpdateOrder(contractId: number, orders: OrderParameter[]): Observable<any> {
    const url = `${this.baseUrl}contracts/${contractId}/contractproducts/updateorder`;
    return this.http.put<any>(url, orders);
  }

  checkCanGoActive(contractId: number, sysContractStatusId: number): Observable<ActivationCheck> {
    const url = `${this.baseUrl}contracts/${contractId}/cangoactive?sysContractStatusId=${sysContractStatusId}`;
    return this.http.post<ActivationCheck>(url, {});
  }

  getFlightCardDetailsById(
    contractId: number,
    flightcarddetailid: number
  ): Observable<FlightCardDetail> {
    const url = `${this.baseUrl}contracts/${contractId}/flightcarddetails/${flightcarddetailid}`;
    return this.http.get<FlightCardDetail>(url);
  }

  getAircraftInWarrantyStatus(contractId: number): Observable<AircraftInWarrantyStatus[]> {
    const url = `${this.baseUrl}contracts/${contractId}/activewarranty`;
    return this.http.get<AircraftInWarrantyStatus[]>(url);
  }

  insertContractReEnroll(form: FormGroup): Observable<number> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')!.value}/reenroll`;
    return this.http.post<number>(url, createInsertBody(form));
  }

  getReport(parameters: ReportRequest): Observable<string> {
    const url = `${this.baseReportUrl}reports/pdf/base64`;
    return this.http.post<string>(url, parameters, {
      responseType: 'text' as 'json',
    });
  }

  updateContractEffectiveExpiryDates(form: UntypedFormGroup): Observable<any> {
    const url = `${this.baseUrl}contracts/${form.get('contractId')?.value}/changeeffectiveexpiry`;
    return this.http.put<any>(url, createInsertBody(form));
  }

  deleteAcctInvoiceById(invoiceId: number): Observable<any> {
    const url = `${this.baseUrl}acctinvoices/${invoiceId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  restoreAcctInvoiceById(invoiceId: number): Observable<any> {
    const url = `${this.baseUrl}acctinvoices/${invoiceId}/restore`;
    return this.http.put<any>(url, {}, { observe: 'response' });
  }

  getTaxSetupIdFromBillingEntity(contractId: number, masterBillToId: number): Observable<number> {
    const url = `${this.baseUrl}contracts/${contractId}/masterbilltos/${masterBillToId}/tax`;
    return this.http.get<number>(url);
  }

  getAdobeToken(code: string, redirectUrl: string): Observable<any> {
    const url = `${this.baseUrl}adobe/esign/token`;
    return this.http.post<any>(url, { code, redirectUrl });
  }

  getAdobeRefreshTokenInfo(): Observable<AdobeRefreshTokenInfo> {
    const url = `${this.baseUrl}adobe/esign/refreshtoken/info`;
    return this.http.get<AdobeRefreshTokenInfo>(url);
  }

  getSummaryToDate(contractId: number, form: FormGroup): Observable<ContractSummaryToDate> {
    let params = new HttpParams();
    Object.entries(form.value).forEach(([key, value]) => {
      params = value ? params.append(key, value.toString()) : params;
    });
    const url = `${this.baseUrl}contracts/${contractId}/anniversarysummary`;
    return this.http.get<ContractSummaryToDate>(url, { params });
  }

  deleteChartNote(chartNoteId: number): Observable<any> {
    const url = `${this.baseUrl}chartnotes/${chartNoteId}`;
    return this.http.delete(url);
  }

  getContractCaptureTypesHistory(contractId: number): Observable<ContractCaptureTypeHistory[]> {
    const url = `${this.baseUrl}contracts/${contractId}/contractcapturetypeshistory`;
    return this.http.get<ContractCaptureTypeHistory[]>(url);
  }

  calculateEstimate(contractId: number, fromDate: string): Observable<EstimateCalculation> {
    const url = `${this.baseUrl}contracts/${contractId}/calculateestimate`;
    return this.http.post<EstimateCalculation>(url, JSON.stringify(fromDate));
  }
}
